import { unstable_createMuiStrictModeTheme as createMuiTheme } from '@material-ui/core';

import type { ThemeOptions } from '@material-ui/core';
import type { Overrides } from '@material-ui/core/styles/overrides';

import { PALETTE_PRIMARY_MAIN_VAR } from './css-vars';

export const palette = {
  primary: {
    light: '#ff9a3a',
    main: '#ec6707',
    dark: '#b93400',
    accent: '#fffaf5',
  },
  secondary: {
    light: '#4f5b62',
    main: '#263238',
    dark: '#000a12',
  },
  info: {
    light: '#0093A1',
    main: '#1F6FED', // 0093A1 oder 1368ED oder 1F6FED
    dark: '#0E336E',
  },
  highlight: '#FF4D4D',
  // color for all intelligent "ki" features
  intelligence: {
    light: '#A78BFA',
    main: '#8B5CF6',
    dark: '#6D28D9',
  },
  // image gallery arrows/fullscreen icon/bullets (CarsTableImageCell) - mirrors primary by default
  galleryIcon: {
    light: '#ff9a3a',
    main: '#ec6707',
    dark: '#b93400',
  },
  // binoculars icon when a car is on the watchlist (WatchlistIcon) - mirrors primary by default
  watchlistIconActive: '#ec6707',
};

// Options that depend on the palette (e.g. heading underline color) are built per-theme via
// createThemeOptions so they follow whichever palette (default or VGRD) is passed in.
// The explicit ThemeOptions return type is load-bearing: it type-checks the override keys below, so a
// misspelled component or rule name (MuiSlidr, raill) is a compile error rather than a silently
// ignored style. Do not replace it with a cast at the call site.
export const createThemeOptions = (themePalette: typeof palette): ThemeOptions => ({
  typography: {
    h1: {
      fontSize: '2.5rem',
      fontWeight: 'bold',
      textDecorationColor: themePalette.primary.main,
      textDecorationLine: 'underline',
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 'bold',
      textDecorationColor: themePalette.primary.main,
      textDecorationLine: 'underline',
    },
    h3: {
      fontSize: '1.25rem',
      fontWeight: 'bold',
      textDecorationColor: themePalette.primary.main,
      textDecorationLine: 'underline',
    },
    fontFamily: ['Inter', 'Roboto'].join(','),
  },
  overrides: {
    // MuiAutocomplete: {
    //   listbox: {
    //     padding: '4px 0',
    //   },
    //   option: {
    //     paddingTop: 0,
    //     paddingRight: 8,
    //     paddingBottom: 0,
    //     paddingLeft: 8,
    //   },
    // },
    MuiTooltip: {
      tooltip: {
        '@media print': {
          display: 'none',
        },
      },
    },
    // mui-datatables 3.8.5 ships an incomplete class-key union: `responsiveBase` exists at runtime
    // (it is the default responsive class) but is missing from its ComponentNameToClassKey. The cast
    // is scoped to this one block so every other override key stays checked.
    MUIDataTable: {
      responsiveBase: {
        paddingLeft: 16,
        paddingRight: 16,
      },
      tableRoot: {
        position: 'relative',
        '&::after': {
          content: '""',
          display: 'block',
          position: 'absolute',
          top: 0,
          right: -16,
          bottom: 0,
          width: 16,
        },
      },
    } as Overrides['MUIDataTable'],
    MuiTableRow: {
      hover: {
        cursor: 'pointer',
        '&:hover .car-row-edit-button': {
          visibility: 'visible',
        },
      },
    },
    MUIDataTableHeadCell: {
      root: {
        fontWeight: 'bold',
        textAlign: 'center',
        lineHeight: '1.25rem',
      },
      hintIconAlone: {
        color: 'rgba(0, 0, 0, 0.54)',
      },
      hintIconWithSortIcon: {
        color: 'rgba(0, 0, 0, 0.54)',
      },
      toolButton: {
        justifyContent: 'center',
      },
    },
    MuiCssBaseline: {
      '@global': {
        ':root': {
          [PALETTE_PRIMARY_MAIN_VAR]: themePalette.primary.main,
        },
        mark: {
          padding: '0 0.25em',
          backgroundColor: themePalette.primary.light,
        },
      },
    },
  },
  props: {
    MuiMenu: {
      // prevent removing focus from usersnap widget
      disableEnforceFocus: true,
    },
    MuiDialog: {
      // prevent removing focus from usersnap widget
      disableEnforceFocus: true,
    },
    MuiDrawer: {
      // prevent removing focus from usersnap widget
      disableEnforceFocus: true,
    },
  },
});

const theme = createMuiTheme({
  palette,
  ...createThemeOptions(palette),
});

export default theme;
